import { HttpClient } from '@angular/common/http';
import { DocumentNode } from 'graphql'
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BaseModel } from '../models/base.model';
import { ColumnInterface } from '../interfaces/column.interface';
import { GraphQLInterface } from '../interfaces/graphql.interface';
import { ModelInterface } from '../interfaces/model.interface';
import { Pagination } from '../models/pagination.model';
import { Language } from '../models/language.model';
import { MatSnackBar } from '@angular/material/snack-bar';

import { print } from 'graphql/language/printer'

export abstract class GraphQLService<T extends BaseModel> {

  baseUrl = environment.graphQL
  lastPagination: Pagination<T> | undefined
  lastAggregations: Language[] | undefined

  protected constructor(
    protected http: HttpClient,
    private snackBar: MatSnackBar
  ) {
  }

  public findAll(query: string | DocumentNode, variables?: {}): Observable<T[]> {
    return this.query({query: query, variables: variables});
  }

  public find(query: string | DocumentNode, variables?: {}): Observable<T> {
    return this.query({query: query, variables: variables});
  }

  public push(mutate: string | DocumentNode, variables?: {}): Observable<T> {
    return this.mutate({query: mutate, variables: variables});
  }

  protected query<T>(options: GraphQLInterface): Observable<T> {

    if (this.instanceOfDocumentNode(options.query)) {
      // convert DocumentNode in a string representation of our query
      options.query = print(options.query)
    }

    return this.http
      .post<{ data: T }>(this.baseUrl, {
        ...options
      })
      .pipe(map((d) => this.toModel(d)));
  }

  protected mutate<T>(options: GraphQLInterface): Observable<T> {

    if (this.instanceOfDocumentNode(options.query)) {
      options.query = print(options.query)
    }

    return this.http
      .post<{ data: T }>(this.baseUrl, {
        ...options
      })
      .pipe(map((d) => this.toModel(d)
      ));
  }

  private toModel(d: any, model?: ModelInterface | undefined) {

    if (Object.keys(d).length === 2 && d.errors) {
      d.errors.forEach(
        (error: { message: string; location: string[] }) => {
          console.error(error.message, error.location)
          this.snackBar.open(error.message, 'OK');
        }
      )
      throw d
    }

    model ||= this.detectModel(d.data)
    const pagination = this.extractPagination(d.data)
    if (pagination) {
      this.lastPagination = pagination
    }
    const aggregations = this.extractAggregations(d.data)
    if (aggregations && aggregations.length > 0) {
      this.lastAggregations = aggregations
    }

    if (typeof model === 'undefined') {
      console.log('no model found for', d.data, d)
      return
    }

    // @ts-ignore
    if (d.data[model.detectedRoot] === null) {
      return
    }

    if (model && typeof model?.detectedRoot !== 'undefined') {
      if (typeof d.data[model?.detectedRoot].map === 'function') { // array
        return d.data[model?.detectedRoot].map((con: any) => {
          if (model) {
            return this.extractModel(model, con)
          }
        })
      } else { // single item
        const con = d.data[model?.detectedRoot];
        return this.extractModel(model, con)
      }
    }
  }

  private extractModel(model: ModelInterface | undefined, data: any) {
    if (model) {
      const obj = new BaseModel.constructors[model.name]()
      obj.columns.forEach((column: ColumnInterface) => {
        if (column.property) {
          if (typeof data[column.column] === 'object' && data[column.column] !== null && !this.isArrayOfStrings(data[column.column])) {
            const fake: { [key: string]: any } = {}
            fake[column.model || column.column] = data[column.column]
            const subModel = this.detectModel(fake)
            if (typeof subModel !== 'undefined' && subModel.detectedRoot) {
              obj[column.property] = this.toModel({data: fake}, subModel)
            } else if (typeof subModel === 'undefined') {
              console.error('No Model detected for', column.property, fake)
            } else {
              console.error('Column', column.property, 'can not be assigned ', fake, subModel)
            }

          } else {
            obj[column.property] = data[column.column];
          }
        }
      })
      return obj
    }
  }

  private detectModel(data: any): ModelInterface | undefined {
    if (typeof data === 'undefined') {
      return
    }

    return Object.keys(data).map((key) => {
      const model = BaseModel.models.find((model) => model.root === key || model.aliases.indexOf(key) > -1)
      if (model) {
        if (model.root === key) {
          model.detectedRoot = key
          return model
        } else {
          let alias = model.aliases.find((_alias) => _alias === key)
          if (alias) {
            model.detectedRoot = alias
            return model
          }
        }
      }
      return undefined
    })[0]
  }

  private extractPagination(data: any): Pagination<T> | undefined {
    if (typeof data === 'undefined') {
      return
    }

    const key = Object.keys(data).find((k) => k === 'pagination')
    if (!key) {
      return
    }
    const model = BaseModel.models.find((model) => model.root === key || model.aliases.indexOf(key) > -1)
    if (!model) {
      return
    }
    return this.extractModel(model, data[key])
  }

  private extractAggregations(data: any): Language[] | undefined {
    if (typeof data === 'undefined') {
      return
    }

    const key = Object.keys(data).find((k) => k === 'aggregations')
    if (!key) {
      return
    }
    const model = BaseModel.models.find((model) => model.root === key || model.aliases.indexOf(key) > -1)
    if (!model) {
      return
    }
    return data[key].map(
      (entry: any) => this.extractModel(model, entry)
    )
  }

  private instanceOfDocumentNode(object: any): object is DocumentNode {
    if (object instanceof String) {
      return false
    }
    return object.kind === 'Document'
  }

  private isArrayOfStrings(array: string[]): Boolean {
    try {
      return !array.find((item: string) => {
        return typeof item !== 'string'
      })
    } catch (e) {
      return false
    }
  }

}
